<template>
  <div>
    <div class="flex justify-between items-center mb-6">
      <h1 class="text-2xl font-bold text-gray-900">Entregadores</h1>
      <button
        @click="openCreateModal"
        class="px-4 py-2 bg-orange-600 text-white rounded-lg hover:bg-orange-700 text-sm font-medium"
      >
        + Novo Entregador
      </button>
    </div>
    
    <div class="bg-white rounded-xl shadow-sm border border-gray-100 overflow-hidden">
      <table class="min-w-full divide-y divide-gray-200">
        <thead class="bg-gray-50">
          <tr>
            <th class="px-6 py-3 text-left text-xs font-medium text-gray-500 uppercase">Nome</th>
            <th class="px-6 py-3 text-left text-xs font-medium text-gray-500 uppercase">Tipo</th>
            <th class="px-6 py-3 text-left text-xs font-medium text-gray-500 uppercase">CPF/CNPJ</th>
            <th class="px-6 py-3 text-left text-xs font-medium text-gray-500 uppercase">Status</th>
            <th class="px-6 py-3 text-left text-xs font-medium text-gray-500 uppercase">Ações</th>
          </tr>
        </thead>
        <tbody class="bg-white divide-y divide-gray-200">
          <tr v-for="driver in drivers" :key="driver.id" class="hover:bg-gray-50">
            <td class="px-6 py-4 font-medium text-gray-900">
              {{ driver.display_name || driver.name }}
            </td>
            <td class="px-6 py-4 text-gray-500">
              <span
                class="inline-flex items-center px-2.5 py-0.5 rounded-full text-xs font-medium"
                :class="driver.driver_type === 'company' ? 'bg-blue-100 text-blue-800' : 'bg-green-100 text-green-800'"
              >
                {{ driver.driver_type === 'company' ? 'Pessoa Jurídica' : 'Pessoa Física' }}
              </span>
            </td>
            <td class="px-6 py-4 text-gray-500">
              {{ driver.cnpj || driver.cpf || '-' }}
            </td>
            <td class="px-6 py-4">
              <button
                @click="toggleActive(driver)"
                class="inline-flex items-center px-2.5 py-0.5 rounded-full text-xs font-medium transition-colors"
                :class="driver.is_active ? 'bg-green-100 text-green-800' : 'bg-gray-100 text-gray-600'"
              >
                {{ driver.is_active ? 'Ativo' : 'Inativo' }}
              </button>
            </td>
            <td class="px-6 py-4">
              <button @click="editDriver(driver)" class="text-blue-600 hover:text-blue-800 font-medium mr-3">
                Editar
              </button>
              <button @click="deleteDriver(driver)" class="text-red-600 hover:text-red-800 font-medium">
                Excluir
              </button>
            </td>
          </tr>
        </tbody>
      </table>
      
      <div v-if="drivers.length === 0" class="p-8 text-center text-gray-500">
        Nenhum entregador cadastrado
      </div>
    </div>
    
    <!-- Create/Edit Driver Modal -->
    <div v-if="showModal" class="fixed inset-0 bg-black/50 flex items-center justify-center z-50 p-4" @click.self="closeModal">
      <div class="bg-white rounded-xl p-6 w-full max-w-lg mx-4 max-h-[90vh] overflow-y-auto">
        <h2 class="text-xl font-bold mb-4">{{ editingId ? 'Editar' : 'Novo' }} Entregador</h2>
        
        <form @submit.prevent="saveDriver">
          <div class="space-y-4">
            <!-- Tipo -->
            <div>
              <label class="block text-sm font-medium text-gray-700 mb-1">Tipo *</label>
              <select v-model="form.driver_type" required class="w-full border rounded-lg px-3 py-2">
                <option value="individual">Pessoa Física</option>
                <option value="company">Pessoa Jurídica</option>
              </select>
            </div>
            
            <!-- Nome (PF apenas) -->
            <div v-if="form.driver_type === 'individual'">
              <label class="block text-sm font-medium text-gray-700 mb-1">Nome *</label>
              <input v-model="form.name" type="text" required class="w-full border rounded-lg px-3 py-2">
            </div>
            
            <!-- CPF (PF) -->
            <div v-if="form.driver_type === 'individual'">
              <label class="block text-sm font-medium text-gray-700 mb-1">CPF *</label>
              <input
                v-model="form.cpf"
                type="text"
                required
                class="w-full border rounded-lg px-3 py-2"
                @input="form.cpf = ($event.target as HTMLInputElement).value.replace(/\D/g, '')"
              >
              <p class="text-xs text-gray-500 mt-1">Somente números</p>
            </div>
            
            <!-- CNPJ (PJ) -->
            <div v-if="form.driver_type === 'company'">
              <label class="block text-sm font-medium text-gray-700 mb-1">CNPJ *</label>
              <input
                v-model="form.cnpj"
                type="text"
                required
                class="w-full border rounded-lg px-3 py-2"
                @input="form.cnpj = ($event.target as HTMLInputElement).value.replace(/\D/g, '')"
              >
              <p class="text-xs text-gray-500 mt-1">Somente números</p>
            </div>
            
            <!-- Nome da Empresa (PJ) -->
            <div v-if="form.driver_type === 'company'">
              <label class="block text-sm font-medium text-gray-700 mb-1">Nome da Empresa *</label>
              <input v-model="form.company_name" type="text" required class="w-full border rounded-lg px-3 py-2">
            </div>
            
            <!-- Motoristas da Empresa (PJ) -->
            <div v-if="form.driver_type === 'company'">
              <div class="flex justify-between items-center mb-2">
                <label class="block text-sm font-medium text-gray-700">Motoristas da Empresa</label>
                <button
                  type="button"
                  @click="addDriverInfo"
                  class="text-xs text-orange-600 hover:text-orange-800 font-medium"
                >
                  + Adicionar motorista
                </button>
              </div>
              
              <div v-for="(info, index) in form.drivers_info" :key="index" class="bg-gray-50 p-3 rounded-lg mb-2">
                <div class="flex justify-between items-center mb-2">
                  <span class="text-xs font-medium text-gray-500">Motorista {{ index + 1 }}</span>
                  <button
                    type="button"
                    @click="removeDriverInfo(index)"
                    class="text-xs text-red-600 hover:text-red-800"
                  >
                    Remover
                  </button>
                </div>
                <div class="grid grid-cols-2 gap-2 mb-2">
                  <input
                    v-model="info.name"
                    type="text"
                    placeholder="Nome"
                    class="border rounded-lg px-3 py-2 text-sm"
                  >
                  <input
                    v-model="info.phone"
                    type="text"
                    placeholder="Telefone"
                    class="border rounded-lg px-3 py-2 text-sm"
                  >
                </div>
                <div class="flex items-center gap-2">
                  <input
                    :value="info.password"
                    type="text"
                    readonly
                    placeholder="Senha"
                    class="flex-1 border rounded-lg px-3 py-2 text-sm bg-gray-100 font-mono"
                  >
                  <button
                    v-if="info.password"
                    type="button"
                    @click="copyDriverPassword(index)"
                    class="px-2 py-2 bg-orange-600 text-white rounded-lg hover:bg-orange-700 text-xs font-medium"
                    title="Copiar senha"
                  >
                    <svg class="w-3 h-3" fill="none" stroke="currentColor" viewBox="0 0 24 24">
                      <path stroke-linecap="round" stroke-linejoin="round" stroke-width="2" d="M8 16H6a2 2 0 01-2-2V6a2 2 0 012-2h8a2 2 0 012 2v2m-6 12h8a2 2 0 002-2v-8a2 2 0 00-2-2h-8a2 2 0 00-2 2v8a2 2 0 002 2z"></path>
                    </svg>
                  </button>
                  <button
                    type="button"
                    @click="regenerateDriverPassword(index)"
                    class="px-2 py-2 bg-blue-600 text-white rounded-lg hover:bg-blue-700 text-xs font-medium"
                    title="Gerar nova senha"
                  >
                    <svg class="w-3 h-3" fill="none" stroke="currentColor" viewBox="0 0 24 24">
                      <path stroke-linecap="round" stroke-linejoin="round" stroke-width="2" d="M4 4v5h.582m15.356 2A8.001 8.001 0 004.582 9m0 0H9m11 11v-5h-.581m0 0a8.003 8.003 0 01-15.357-2m15.357 2H15"></path>
                    </svg>
                  </button>
                </div>
              </div>
              
              <div v-if="form.drivers_info.length === 0" class="text-sm text-gray-400 italic">
                Nenhum motorista cadastrado
              </div>
            </div>
            
            <!-- Senha (exibe ao editar) -->
            <div v-if="editingId">
              <label class="block text-sm font-medium text-gray-700 mb-1">Senha de Acesso</label>
              <div class="flex items-center gap-2">
                <input
                  v-model="form.password"
                  type="text"
                  readonly
                  :placeholder="form.password ? '' : 'Nenhuma senha definida'"
                  class="flex-1 border rounded-lg px-3 py-2 bg-gray-100 text-gray-700 font-mono"
                >
                <button
                  v-if="form.password"
                  type="button"
                  @click="copyEditPassword"
                  class="px-3 py-2 bg-orange-600 text-white rounded-lg hover:bg-orange-700 text-sm font-medium"
                  title="Copiar senha"
                >
                  <svg class="w-4 h-4" fill="none" stroke="currentColor" viewBox="0 0 24 24">
                    <path stroke-linecap="round" stroke-linejoin="round" stroke-width="2" d="M8 16H6a2 2 0 01-2-2V6a2 2 0 012-2h8a2 2 0 012 2v2m-6 12h8a2 2 0 002-2v-8a2 2 0 00-2-2h-8a2 2 0 00-2 2v8a2 2 0 002 2z"></path>
                  </svg>
                </button>
                <button
                  type="button"
                  @click="generateNewPassword"
                  class="px-3 py-2 bg-blue-600 text-white rounded-lg hover:bg-blue-700 text-sm font-medium"
                  title="Gerar nova senha"
                >
                  <svg class="w-4 h-4" fill="none" stroke="currentColor" viewBox="0 0 24 24">
                    <path stroke-linecap="round" stroke-linejoin="round" stroke-width="2" d="M4 4v5h.582m15.356 2A8.001 8.001 0 004.582 9m0 0H9m11 11v-5h-.581m0 0a8.003 8.003 0 01-15.357-2m15.357 2H15"></path>
                  </svg>
                </button>
              </div>
            </div>
            
            <!-- Status -->
            <div>
              <label class="flex items-center space-x-2">
                <input v-model="form.is_active" type="checkbox" class="rounded">
                <span class="text-sm font-medium text-gray-700">Ativo</span>
              </label>
            </div>
          </div>
          
          <div class="flex justify-end gap-3 mt-6">
            <button type="button" @click="closeModal" class="px-4 py-2 text-gray-600 hover:text-gray-800">
              Cancelar
            </button>
            <button
              type="submit"
              :disabled="saving"
              class="px-4 py-2 bg-orange-600 text-white rounded-lg hover:bg-orange-700 disabled:opacity-50"
            >
              {{ saving ? 'Salvando...' : 'Salvar' }}
            </button>
          </div>
        </form>
      </div>
    </div>
    
    <!-- Modal de Senha Gerada -->
    <div v-if="showPasswordModal" class="fixed inset-0 bg-black/50 flex items-center justify-center z-50 p-4" @click.self="closePasswordModal">
      <div class="bg-white rounded-xl max-w-sm w-full p-6 shadow-xl text-center">
        <div class="mx-auto flex items-center justify-center w-12 h-12 bg-green-100 rounded-full mb-4">
          <svg class="w-6 h-6 text-green-600" fill="none" stroke="currentColor" viewBox="0 0 24 24">
            <path stroke-linecap="round" stroke-linejoin="round" stroke-width="2" d="M5 13l4 4L19 7"></path>
          </svg>
        </div>
        <h3 class="text-lg font-bold text-gray-900 mb-2">Entregador criado com sucesso!</h3>
        <p class="text-sm text-gray-500 mb-4">A senha de acesso foi gerada automaticamente.</p>
        
        <div class="bg-gray-100 p-3 rounded-lg mb-4">
          <p class="text-xs text-gray-500 uppercase mb-1">Senha de acesso</p>
          <p class="text-2xl font-mono font-bold text-gray-900 tracking-widest">{{ generatedPassword }}</p>
        </div>
        
        <div class="flex gap-3">
          <button
            @click="copyPassword"
            class="flex-1 px-4 py-2 bg-orange-600 text-white rounded-lg hover:bg-orange-700 text-sm font-medium"
          >
            Copiar senha
          </button>
          <button
            @click="closePasswordModal"
            class="flex-1 px-4 py-2 border border-gray-300 text-gray-700 rounded-lg hover:bg-gray-50 text-sm font-medium"
          >
            Fechar
          </button>
        </div>
      </div>
    </div>
  </div>
</template>

<script setup lang="ts">
import { ref, onMounted } from 'vue'
import { useAuthStore } from '@/stores'
import { driverApi } from '@/utils/api'
import { notifyError, notifyInfo, notifySuccess } from '@/utils/notifications'
import type { DeliveryDriver } from '@/types'
import { confirmDialog } from '@/composables/useConfirm'

const authStore = useAuthStore()

const drivers = ref<DeliveryDriver[]>([])
const showModal = ref(false)
const editingId = ref<number | null>(null)
const saving = ref(false)
const generatedPassword = ref('')
const showPasswordModal = ref(false)

const defaultForm = {
  driver_type: 'individual' as 'individual' | 'company',
  name: '',
  cpf: '',
  cnpj: '',
  company_name: '',
  drivers_info: [] as { name: string; phone: string; license_number?: string; password?: string }[],
  is_active: true,
  password: ''
}

const form = ref({ ...defaultForm })

const loadDrivers = async () => {
  const companyId = authStore.companyId || 1
  try {
    const res = await driverApi.getByCompany(companyId)
    drivers.value = res.data
  } catch (error) {
    console.error('Erro ao carregar entregadores:', error)
  }
}

const openCreateModal = () => {
  editingId.value = null
  form.value = { ...defaultForm }
  showModal.value = true
}

const editDriver = (driver: DeliveryDriver) => {
  editingId.value = driver.id
  form.value = {
    driver_type: driver.driver_type,
    name: driver.name,
    cpf: driver.cpf || '',
    cnpj: driver.cnpj || '',
    company_name: driver.company_name || '',
    drivers_info: driver.drivers_info?.map(d => ({ name: d.name, phone: d.phone || '', license_number: d.license_number || '', password: d.password || '' })) || [],
    is_active: driver.is_active,
    password: driver.password || ''
  }
  showModal.value = true
}

const closeModal = () => {
  showModal.value = false
  editingId.value = null
  form.value = { ...defaultForm }
}

const closePasswordModal = () => {
  showPasswordModal.value = false
  generatedPassword.value = ''
}

const copyPassword = () => {
  if (generatedPassword.value) {
    navigator.clipboard.writeText(generatedPassword.value)
    notifySuccess('Senha copiada para a área de transferência!')
  }
}

const copyEditPassword = () => {
  if (form.value.password) {
    navigator.clipboard.writeText(form.value.password)
    notifySuccess('Senha copiada para a área de transferência!')
  }
}

const generateNewPassword = () => {
  const newPass = Math.floor(10000000 + Math.random() * 90000000).toString()
  form.value.password = newPass
  notifyInfo(`Nova senha gerada: ${newPass}. Clique em "Salvar" para confirmar a alteração.`)
}

const generateDriverPassword = () => {
  return Math.floor(10000000 + Math.random() * 90000000).toString()
}

const copyDriverPassword = (index: number) => {
  const pwd = form.value.drivers_info[index]?.password
  if (pwd) {
    navigator.clipboard.writeText(pwd)
    notifySuccess('Senha do motorista copiada!')
  }
}

const regenerateDriverPassword = (index: number) => {
  const newPass = generateDriverPassword()
  form.value.drivers_info[index].password = newPass
  notifyInfo(`Nova senha gerada para o motorista: ${newPass}. Clique em "Salvar" para confirmar.`)
}

const addDriverInfo = () => {
  form.value.drivers_info.push({ name: '', phone: '', password: generateDriverPassword() })
}

const removeDriverInfo = (index: number) => {
  form.value.drivers_info.splice(index, 1)
}

const formatErrorMessage = (error: any): string => {
  if (error.response?.data?.detail) {
    const detail = error.response.data.detail
    if (typeof detail === 'string') return detail
    if (Array.isArray(detail)) {
      return detail.map((err: any) => {
        if (typeof err === 'string') return err
        const loc = err.loc?.join(' → ') || ''
        const msg = err.msg || JSON.stringify(err)
        return loc ? `${loc}: ${msg}` : msg
      }).join('\n')
    }
    if (typeof detail === 'object') {
      return JSON.stringify(detail, null, 2)
    }
  }
  return error.message || 'Erro ao salvar entregador'
}

const saveDriver = async () => {
  saving.value = true
  try {
    const companyId = authStore.companyId || 1
    const payload = {
      ...form.value,
      company_id: companyId,
      name: form.value.driver_type === 'company' ? (form.value.company_name || 'Empresa') : form.value.name,
      cpf: form.value.cpf || undefined,
      cnpj: form.value.cnpj || undefined,
      company_name: form.value.company_name || undefined,
      password: form.value.password || undefined,
      drivers_info: form.value.drivers_info.filter(d => d.name.trim())
    }
    
    if (editingId.value) {
      await driverApi.update(editingId.value, payload)
      notifySuccess('Entregador atualizado com sucesso!')
      closeModal()
      loadDrivers()
    } else {
      const res = await driverApi.create(payload)
      generatedPassword.value = res.data.password || ''
      closeModal()
      loadDrivers()
      showPasswordModal.value = true
    }
  } catch (error: any) {
    notifyError(formatErrorMessage(error))
  } finally {
    saving.value = false
  }
}

const toggleActive = async (driver: DeliveryDriver) => {
  try {
    await driverApi.update(driver.id, { is_active: !driver.is_active })
    loadDrivers()
  } catch (error: any) {
    notifyError(error.response?.data?.detail || 'Erro ao atualizar status')
  }
}

const deleteDriver = async (driver: DeliveryDriver) => {
  if (!await confirmDialog({ title: 'Confirmar exclusão', message: `Tem certeza que deseja excluir o entregador "${driver.name}"?`, danger: true, confirmLabel: 'Excluir' })) return
  
  try {
    await driverApi.delete(driver.id)
    loadDrivers()
    notifySuccess('Entregador excluído com sucesso!')
  } catch (error: any) {
    notifyError(error.response?.data?.detail || 'Erro ao excluir entregador')
  }
}

onMounted(() => {
  loadDrivers()
})
</script>
